import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Sparkles, Heart, Users, CheckCircle } from 'lucide-react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { PageHero, SectionHeading } from '../components/PageBits';
import { Button } from '../components/ui/button';
import { mockData } from '../data/mock';
import TestimonialStrip from '../components/TestimonialStrip';

const Weddings = () => (
  <div className="min-h-screen bg-cream">
    <Nav transparent />
    <PageHero
      eyebrow="A Private Luxury Estate"
      title="Weddings at Queensland Ranch"
      subtitle="Where your dream day comes to life beneath the willow tree, beside the pool, and under a canopy of string lights."
      image="/hero/hero-main.jpg"
    />

    {/* Intro */}
    <section className="py-20 bg-cream texture-linen">
      <div className="container mx-auto px-6 max-w-4xl">
        <SectionHeading
          eyebrow="The Setting"
          title="A Ceremony"
          accent="Nature Designed"
          subtitle="Seven private acres in Anthony, New Mexico — where Mediterranean architecture, mature landscaping, and a legendary willow tree set the stage for the wedding you've been imagining."
        />
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {[
            { icon: Heart, title: 'Willow Tree Ceremony', desc: 'An intimate open-air aisle beneath a canopy of willow branches.' },
            { icon: Sparkles, title: 'String-Lit Reception', desc: 'Cascades of overhead lights, checkered dance floor, and a Mediterranean backdrop.' },
            { icon: Users, title: 'Up to 130 Guests', desc: 'From intimate 30-guest gatherings to grand 130-guest celebrations.' },
          ].map((f, i) => (
            <div key={i} className="bg-ivory border border-gold/25 p-8 rounded-lg text-center shadow-sm hover:shadow-lg transition-all">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-navy flex items-center justify-center ring-2 ring-gold/40 ring-offset-4 ring-offset-ivory">
                <f.icon className="h-6 w-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-navy mb-2 font-bold">{f.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Real weddings preview */}
    <section className="py-20 bg-cream texture-linen">
      <div className="container mx-auto px-6">
        <SectionHeading eyebrow="Real Couples · Real Moments" title="Real Weddings at" accent="Queensland Ranch" />
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-3">
          {mockData.realWeddings.slice(0, 6).map((src, i) => (
            <div key={i} className={`relative overflow-hidden rounded-lg shadow-xl border border-gold/20 group ${i === 0 ? 'col-span-2 md:col-span-2 md:row-span-2 aspect-[4/3]' : 'aspect-square'}`}>
              <img src={src} alt={`Real wedding ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </div>
    </section>

    <TestimonialStrip title="Loved by Real" accent="Couples" />

    {/* CTA strip */}
    <section className="py-20 bg-ivory texture-linen">
      <div className="container mx-auto px-6 text-center">
        <SectionHeading eyebrow="Your Day, Beautifully Planned" title="Let's Begin with a" accent="Private Tour" subtitle="Every wedding at Queensland Ranch starts with a walk through the grounds. Come see it for yourself." />
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
          <Link to="/contact"><Button size="lg" className="bg-gold hover:bg-gold/90 text-navy font-semibold px-10 py-6 shadow-xl"><Calendar className="mr-2 h-5 w-5" />Check Availability</Button></Link>
          <Link to="/contact"><Button size="lg" variant="outline" className="border-navy/40 text-navy hover:bg-navy hover:text-gold px-10 py-6">Schedule a Tour</Button></Link>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export default Weddings;
